import { Box, Typography, Grid, TextField } from "@mui/material";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { PaymentMethod } from "generated-graphql/graphql";
import { useEffect, useRef } from "react";
import { Control, UseFormWatch, UseFormSetValue } from "react-hook-form";

import { safeParseFloat } from "../lib/numberUtils";
import { PayeeFormInputs } from "../lib/types";

// Helper to format total amount (amount + transactionFee)
function formatTotalAmount(
  amount: number | string | undefined,
  transactionFee: number | string | undefined
): string {
  const amountNum = safeParseFloat(String(amount ?? 0)) ?? 0;
  const feeNum = safeParseFloat(String(transactionFee ?? 0)) ?? 0;
  const total = amountNum + feeNum;
  return total.toFixed(2);
}

interface PaymentSectionProps {
  control: Control<PayeeFormInputs>;
  isLob: boolean;
  notEditable: boolean;
  billingMetadataEditable?: boolean;
  setReportAmounts: () => void;
  watch: UseFormWatch<PayeeFormInputs>;
  setValue: UseFormSetValue<PayeeFormInputs>;
}

export function PaymentSection({
  control,
  isLob,
  notEditable,
  billingMetadataEditable = true,
  setReportAmounts,
  watch,
  setValue,
}: PaymentSectionProps) {
  const billable = watch("billable");
  const previousBillable = useRef<boolean | undefined>(undefined);

  // Clear exception note when billing status changes from false to true
  useEffect(() => {
    if (previousBillable.current === false && billable === true) {
      setValue("comment", "");
    }
    previousBillable.current = billable;
  }, [billable, setValue]);

  // Determine if billing metadata should be disabled
  const billingDisabled = !billingMetadataEditable;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Payment
      </Typography>
      <Grid container spacing={2}>
        {/* First row: Memo and Invoice # */}
        <Grid size={6}>
          <FormTextField
            name="memo"
            label="Memo"
            maxLength={isLob ? 40 : undefined}
            control={control}
            disabled={notEditable}
          />
        </Grid>
        <Grid size={6}>
          <FormTextField
            name="invoiceNumber"
            label="Invoice #"
            control={control}
            disabled={notEditable}
          />
        </Grid>

        {/* Second row: Payment Method */}
        <Grid size={12}>
          <FormSelect
            name="paymentMethod"
            label="Payment Method"
            control={control}
            selectItems={[
              { value: PaymentMethod.Check, display: "Check" },
              {
                value: PaymentMethod.CreditCard,
                display: "Credit Card",
              },
              { value: PaymentMethod.Ach, display: "ACH" },
            ]}
            disabled={isLob || notEditable}
          />
        </Grid>

        {/* Third row: Amount, Transaction Fee, and Total Amount */}
        <Grid size={4}>
          <FormTextField
            name="amount"
            label="Amount"
            control={control}
            onBlur={setReportAmounts}
            disabled={notEditable}
          />
        </Grid>
        <Grid size={4}>
          <FormTextField
            name="transactionFee"
            label="Transaction Fee"
            control={control}
            disabled={notEditable}
          />
        </Grid>
        <Grid size={4}>
          <TextField
            label="Total Amount"
            value={formatTotalAmount(watch("amount"), watch("transactionFee"))}
            disabled
            fullWidth
            InputProps={{ readOnly: true }}
          />
        </Grid>

        {/* Customer Billing - full width */}
        <Grid size={12}>
          <FormSelect
            name="billable"
            label="Customer Billing"
            control={control}
            selectItems={[
              { value: true, display: "Billable to Customer" },
              {
                value: false,
                display: "Exclude from Customer Billing",
              },
            ]}
            disabled={billingDisabled}
          />
        </Grid>

        {/* Exception Note - conditional full width */}
        {watch("billable") === false && (
          <Grid size={12}>
            <FormTextField
              name="comment"
              label="Exception Note (required)"
              control={control}
              disabled={billingDisabled}
              textFieldProps={{
                multiline: true,
                rows: 3,
                placeholder: "Please explain why this payment is not billable...",
              }}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
